import type { RiskBoundsConfig, RiskModelWithDetails } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api/_client";
import { useUpdateRiskModel } from "@/lib/hooks/use-risk-models";
import { useT } from "@/lib/i18n/use-t";
import { getScoreRange } from "@/lib/utils/risk-scoring";
import { T } from "./risk-bounds-editor.i18n";

type DraftRow = { maxEquitySharePct: string; maxVolatilityPct: string };

function draftFromConfig(
  config: RiskBoundsConfig | null,
  scores: number[],
): Record<number, DraftRow> {
  const byScore = new Map((config?.bounds ?? []).map((b) => [b.score, b]));
  const rows: Record<number, DraftRow> = {};
  for (const score of scores) {
    const entry = byScore.get(score);
    rows[score] = {
      maxEquitySharePct: entry?.maxEquitySharePct?.toString() ?? "",
      maxVolatilityPct: entry?.maxVolatilityPct?.toString() ?? "",
    };
  }
  return rows;
}

/**
 * GAP-028 — per-risk-score ceilings (max equity share + max volatility) that a
 * CUSTOM portfolio must respect. Saved as one riskBounds blob on the model; the
 * server validates full coverage / non-decreasing values / null propagation.
 */
export function RiskBoundsEditor({
  model,
  onError,
}: {
  model: RiskModelWithDetails;
  onError: (title: string, description: string) => void;
}) {
  const t = useT(T);
  const updateModel = useUpdateRiskModel();

  const range = getScoreRange(model.matrixScores);
  const hasMatrix = (model.matrixScores?.length ?? 0) > 0;
  const scores: number[] = [];
  for (let s = range.min; s <= range.max; s++) scores.push(s);

  const [enabled, setEnabled] = useState(model.riskBounds?.enabled ?? false);
  const [deviationAcceptance, setDeviationAcceptance] = useState(
    model.riskBounds?.deviationAcceptanceEnabled ?? false,
  );
  const [rows, setRows] = useState<Record<number, DraftRow>>(() =>
    draftFromConfig(model.riskBounds ?? null, scores),
  );

  // Re-sync drafts when the model reloads (e.g. after save).
  useEffect(() => {
    setEnabled(model.riskBounds?.enabled ?? false);
    setDeviationAcceptance(model.riskBounds?.deviationAcceptanceEnabled ?? false);
    setRows(draftFromConfig(model.riskBounds ?? null, scores));
  }, [model.riskBounds]);

  const setCell = (score: number, key: keyof DraftRow, value: string) => {
    setRows((prev) => ({ ...prev, [score]: { ...prev[score]!, [key]: value } }));
  };

  const handleSave = async () => {
    const config: RiskBoundsConfig | null =
      enabled ||
      deviationAcceptance ||
      scores.some((s) => rows[s]?.maxEquitySharePct || rows[s]?.maxVolatilityPct)
        ? {
            enabled,
            deviationAcceptanceEnabled: deviationAcceptance,
            bounds: scores.map((score) => ({
              score,
              maxEquitySharePct:
                rows[score]?.maxEquitySharePct.trim() !== ""
                  ? Number.parseFloat(rows[score]!.maxEquitySharePct)
                  : null,
              maxVolatilityPct:
                rows[score]?.maxVolatilityPct.trim() !== ""
                  ? Number.parseFloat(rows[score]!.maxVolatilityPct)
                  : null,
            })),
          }
        : null;
    try {
      await updateModel.mutateAsync({
        id: model.id,
        data: {
          nameI18n: model.nameI18n,
          descriptionI18n: model.descriptionI18n ?? undefined,
          riskBounds: config,
        },
      });
      // QL-118 — unified save confirmation, matching the toast pattern used by
      // the other settings editors.
      toast.success(t.saved);
    } catch (err) {
      onError(t.error, err instanceof ApiError ? err.message : t.failedToSave);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="risk-bounds-enabled">{t.enable}</Label>
            <p className="text-xs text-muted-foreground">{t.enableHint}</p>
          </div>
          <Switch id="risk-bounds-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="risk-bounds-deviation">{t.deviationAcceptance}</Label>
            <p className="text-xs text-muted-foreground">
              {t.deviationAcceptanceHint}
              {!enabled && ` ${t.deviationAcceptanceLocked}`}
            </p>
          </div>
          <Switch
            id="risk-bounds-deviation"
            checked={deviationAcceptance}
            onCheckedChange={setDeviationAcceptance}
            disabled={!enabled}
          />
        </div>

        {hasMatrix ? (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.score}</TableHead>
                <TableHead className="text-right">{t.maxEquity}</TableHead>
                <TableHead className="text-right">{t.maxVolatility}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scores.map((score) => (
                <TableRow key={score}>
                  <TableCell className="font-medium">{score}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      className="w-28 ml-auto text-right"
                      aria-label={`${t.maxEquity} — ${t.score} ${score}`}
                      value={rows[score]?.maxEquitySharePct ?? ""}
                      onChange={(e) => setCell(score, "maxEquitySharePct", e.target.value)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      className="w-28 ml-auto text-right"
                      aria-label={`${t.maxVolatility} — ${t.score} ${score}`}
                      value={rows[score]?.maxVolatilityPct ?? ""}
                      onChange={(e) => setCell(score, "maxVolatilityPct", e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">{t.noMatrix}</p>
        )}

        <p className="text-xs text-muted-foreground">{t.boundsHint}</p>

        <div className="flex justify-end">
          <Button onClick={handleSave} loading={updateModel.isPending}>
            {t.save}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
